/**
 * DocumentGateSection — Locked placeholder shown in the workspace where
 * DocumentFlowPanel will appear once the offer is accepted.
 *
 * Renders when offerStatus is 'pending' or 'countered'.
 *
 * @param {Object} props
 * @param {string} props.offerStatus      - 'pending' | 'countered'
 * @param {string} [props.propertyAddress] - human-readable address for copy
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './DocumentGateSection.css';

const DOC_STEPS = [
  { key: 'accept',    label: 'Accept' },
  { key: 'generate',  label: 'Generate' },
  { key: 'sign',      label: 'Sign' },
  { key: 'done',      label: 'Done' },
];

const NEXT_STEPS = [
  'Both parties accept the offer',
  'Generate your purchase agreement',
  'Both parties sign digitally',
];

/**
 * Decorative greyed-out step bar (aria-hidden — purely decorative).
 */
const GhostStepBar = () => (
  <div className="doc-gate__ghost-bar" aria-hidden="true">
    {DOC_STEPS.map((step, idx) => (
      <React.Fragment key={step.key}>
        {idx > 0 && <span className="doc-gate__ghost-connector" />}
        <span className="doc-gate__ghost-pip">
          <span className="doc-gate__ghost-dot" />
          <span className="doc-gate__ghost-label">{step.label}</span>
        </span>
      </React.Fragment>
    ))}
  </div>
);

const DocumentGateSection = ({ offerStatus, propertyAddress }) => {
  const [accordionOpen, setAccordionOpen] = useState(false);
  const accordionBodyRef = useRef(null);
  const triggerRef = useRef(null);

  // Map offer status to human-readable context hint
  const statusHint =
    offerStatus === 'countered'
      ? 'A counter-offer is in progress. Documents unlock when both parties agree on final terms.'
      : 'Documents become available once both parties accept the offer.';

  const toggleAccordion = useCallback(() => {
    setAccordionOpen((prev) => !prev);
  }, []);

  // Keyboard: Escape closes the accordion
  useEffect(() => {
    if (!accordionOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setAccordionOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [accordionOpen]);

  const accordionId = 'doc-gate-accordion-body';
  const triggerId = 'doc-gate-accordion-trigger';

  return (
    <section
      className="doc-gate"
      aria-label="Documents — not yet available"
    >
      {/* Header row */}
      <div className="doc-gate__header">
        <span className="doc-gate__lock-icon" aria-hidden="true">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <rect
              x="4"
              y="10"
              width="16"
              height="12"
              rx="2.5"
              stroke="#9ca3af"
              strokeWidth="1.75"
            />
            <path
              d="M8 10V7a4 4 0 018 0v3"
              stroke="#9ca3af"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
            <circle cx="12" cy="16" r="1.5" fill="#9ca3af" />
          </svg>
        </span>
        <h3 className="doc-gate__title">Documents</h3>
      </div>

      {/* Body copy — communicates "not yet" in text, not just via visual lock */}
      <p className="doc-gate__body">
        {propertyAddress
          ? <>Documents for <strong>{propertyAddress}</strong> become available once both parties accept the offer. You&apos;ll be guided through generating and signing your purchase agreement.</>
          : statusHint + ' You\'ll be guided through generating and signing your purchase agreement.'
        }
      </p>

      {/* Accordion: What happens next? */}
      <div className="doc-gate__accordion">
        <button
          id={triggerId}
          ref={triggerRef}
          className="doc-gate__accordion-trigger"
          type="button"
          aria-expanded={accordionOpen}
          aria-controls={accordionId}
          onClick={toggleAccordion}
        >
          <span className="doc-gate__accordion-arrow" aria-hidden="true">
            {accordionOpen ? '▾' : '▸'}
          </span>
          What happens next?
        </button>

        <div
          id={accordionId}
          role="region"
          aria-labelledby={triggerId}
          className={`doc-gate__accordion-body${accordionOpen ? ' doc-gate__accordion-body--open' : ''}`}
          ref={accordionBodyRef}
          hidden={!accordionOpen}
        >
          <ol className="doc-gate__steps-list">
            {NEXT_STEPS.map((step, i) => (
              <li key={i} className="doc-gate__steps-item">
                <span className="doc-gate__steps-num" aria-hidden="true">{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Decorative ghost step bar — aria-hidden */}
      <GhostStepBar />
    </section>
  );
};

DocumentGateSection.propTypes = {
  offerStatus: PropTypes.string,
  propertyAddress: PropTypes.string,
};

DocumentGateSection.defaultProps = {
  offerStatus: 'pending',
  propertyAddress: '',
};

export default DocumentGateSection;
